import { Badge, Button, Input, useToast } from '@/components';
import {
  ApiClientError,
  GITHUB_USERNAME_RE,
  normalizeGithubUsername,
} from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { useUpdateMe } from './hooks';

export interface VerifiedGithub {
  login: string;
  name: string | null;
  avatarUrl: string | null;
}

export const ProfileForm = (props: {
  initialName: string;
  initialGithub: string | null;
  verifiedGithub?: VerifiedGithub | null;
  locked?: boolean;
}) => {
  const update = useUpdateMe();
  const toast = useToast();
  const [name, setName] = createSignal(props.initialName);
  const [github, setGithub] = createSignal(props.initialGithub ?? '');
  const [error, setError] = createSignal<string | null>(null);

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    // A locked username is read-only for the owner — never send the field.
    let body: { displayName: string; githubUsername?: string | null } = {
      displayName: name().trim(),
    };
    if (!props.locked) {
      const gh = normalizeGithubUsername(github());
      setGithub(gh);
      if (gh !== '' && !GITHUB_USERNAME_RE.test(gh)) {
        setError('Invalid GitHub username.');
        return;
      }
      body = { ...body, githubUsername: gh === '' ? null : gh };
    }
    try {
      await update.mutateAsync(body);
      toast.success('Profile updated');
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.error.message);
      } else {
        setError((err as Error).message ?? 'Update failed');
      }
    }
  };

  return (
    <form onSubmit={onSubmit} class="flex flex-col gap-4">
      <Input
        label="Display name"
        required
        value={name()}
        onInput={(e) => setName(e.currentTarget.value)}
      />
      <Input
        label="GitHub username"
        hint={
          props.locked
            ? 'Locked by an administrator — contact an admin to change it'
            : 'Used to map your work in the system, e.g. uchatzimp — pasting a profile URL works too'
        }
        placeholder="uchatzimp"
        value={github()}
        onInput={(e) => setGithub(e.currentTarget.value)}
        disabled={props.locked}
        class="disabled:cursor-not-allowed disabled:opacity-50"
      />
      <Show when={props.verifiedGithub}>
        {(gh) => (
          <div class="flex items-center gap-2 text-sm text-neutral-700 transition-opacity duration-200 ease-out starting:opacity-0 motion-reduce:transition-none dark:text-neutral-300">
            <Show when={gh().avatarUrl}>
              <img src={gh().avatarUrl ?? ''} alt="" class="h-5 w-5 rounded-full" />
            </Show>
            <span>{gh().name ?? gh().login}</span>
            <Badge>Verified</Badge>
          </div>
        )}
      </Show>
      <Show when={error()}>
        <p class="text-xs text-red-600">{error()}</p>
      </Show>
      <div class="flex justify-end">
        <Button type="submit" loading={update.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
};
